import { useEffect } from "react";

const SITE_URL = "https://taxshift2026.com";
const DEFAULT_TITLE = "TaxShift 2026 — OBBBA Tax Navigator · Free 2026 Tax Calculators";
const DEFAULT_DESC =
  "Free 2026 federal tax calculators for OBBBA's new deductions: No Tax on Tips, No Tax on Overtime, $6,000 Senior Deduction, $40k SALT cap, and more.";

const setMeta = (name, content, attr = "name") => {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const setLink = (rel, href) => {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

const setJsonLd = (id, data) => {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
};

/**
 * useSEO — dynamically updates <title>, meta tags, canonical URL, and JSON-LD for each route.
 */
export function useSEO({
  title,
  description,
  keywords,
  path = "/",
  ogImage,
  type = "website",
  jsonLd = null,
}) {
  useEffect(() => {
    const fullTitle = title ? `${title} | TaxShift 2026` : DEFAULT_TITLE;
    const desc = description || DEFAULT_DESC;
    const url = `${SITE_URL}${path}`;

    document.title = fullTitle;

    setMeta("description", desc);
    if (keywords) setMeta("keywords", keywords);
    setMeta("robots", "index, follow, max-image-preview:large");

    setMeta("og:title", fullTitle, "property");
    setMeta("og:description", desc, "property");
    setMeta("og:url", url, "property");
    setMeta("og:type", type, "property");
    setMeta("og:site_name", "TaxShift 2026", "property");
    if (ogImage) setMeta("og:image", ogImage, "property");

    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", fullTitle);
    setMeta("twitter:description", desc);
    setMeta("twitter:url", url);
    if (ogImage) setMeta("twitter:image", ogImage);

    setLink("canonical", url);

    if (jsonLd) {
      setJsonLd("page-jsonld", jsonLd);
    } else {
      const existing = document.getElementById("page-jsonld");
      if (existing) existing.remove();
    }

    window.scrollTo({ top: 0, behavior: "instant" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, keywords, path, ogImage, type, JSON.stringify(jsonLd)]);
}

export const buildCalculatorSchema = ({ name, description, url }) => ({
  "@context": "https://schema.org",
  "@type": "WebApplication",
  name,
  description,
  url: `${SITE_URL}${url}`,
  applicationCategory: "FinanceApplication",
  operatingSystem: "Any",
  offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
});

export const buildArticleSchema = ({ title, description, author, datePublished, slug }) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  headline: title,
  description,
  author: { "@type": "Person", name: author },
  publisher: {
    "@type": "Organization",
    name: "TaxShift 2026",
    logo: { "@type": "ImageObject", url: `${SITE_URL}/logo.png` },
  },
  datePublished,
  dateModified: datePublished,
  mainEntityOfPage: `${SITE_URL}/blog/${slug}`,
});

export const buildFAQSchema = (qa) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: qa.map(({ q, a }) => ({
    "@type": "Question",
    name: q,
    acceptedAnswer: { "@type": "Answer", text: a },
  })),
});

export const buildBreadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: `${SITE_URL}${it.path}`,
  })),
});
